import { createFileRoute } from "@tanstack/react-router";
import { Breadcrumbs, PageHeader, Section, SectionHeading } from "@/components/site/section";
import { CtaLink } from "@/components/site/cta";
import { FinalCTA } from "@/components/site/final-cta";
import { pageUrl } from "@/data/site";

export const Route = createFileRoute("/fordite-and-colorwood")({
  head: () => ({
    meta: [
      { title: "Fordite & Italian Colorwood Pen Blanks | Paul's Pens n Things" },
      {
        name: "description",
        content:
          "Shop-made fordite pen blanks in 70 to 90 powder-coat layers, plus Italian colorwood blanks. 3/4 inch at $35, 15/16 inch at $45, colorwood at $8.95.",
      },
      { property: "og:title", content: "Fordite & Italian colorwood pen blanks" },
      {
        property: "og:description",
        content:
          "Seven-colour, 70-90 layer fordite poured in Paul's shop, and angle-cut Italian colorwood blanks for turners.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: pageUrl("/fordite-and-colorwood") },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: pageUrl("/fordite-and-colorwood") }],
  }),
  component: ForditeAndColorwood,
});

type Blank = {
  name: string;
  price: string;
  image: string;
  alt: string;
  spec: string;
};

const fordite: Blank[] = [
  {
    name: "3/4 Fordite pen blank",
    price: "$35",
    image: "/fordite-pen-blank-three-quarter.webp",
    alt: "Shop-made fordite pen blank, 3/4 inch square, showing layered red, green and cream powder-coat strata",
    spec: "3/4 x 3/4 x 5+ inches, includes shipping",
  },
  {
    name: "15/16 Fordite pen blank",
    price: "$45",
    image: "/fordite-pen-blank-fifteen-sixteenths.webp",
    alt: "Shop-made fordite pen blank, 15/16 inch square, with swirled red, green and grey paint layers",
    spec: "15/16 x 15/16 x 5+ inches, includes shipping.",
  },
];

const colorwood: Blank[] = [
  {
    name: "Italian colorwood pen blank",
    price: "$8.95",
    image: "/italian-colorwood-pen-blank.webp",
    alt: "Italian colorwood pen blank with angled red, green and cream laminated veneer layers",
    spec: "3/4 x 3/4 x 6 inches segmented wood pen blank",
  },
];

function BlankGrid({ items }: { items: Blank[] }) {
  return (
    <ul className="mt-12 grid gap-px border border-hairline bg-hairline sm:grid-cols-2 lg:grid-cols-3">
      {items.map((b) => (
        <li key={b.name} className="flex flex-col bg-background">
          <div className="overflow-hidden bg-paper">
            <img
              src={b.image}
              alt={b.alt}
              loading="lazy"
              width={360}
              height={271}
              className="h-full w-full object-cover motion-craft transition-transform duration-700 hover:scale-[1.03]"
            />
          </div>
          <div className="flex flex-1 flex-col p-7 sm:p-8">
            <h3 className="font-[family-name:var(--font-display)] text-2xl leading-tight">{b.name}</h3>
            <p className="mt-2 text-lg font-semibold text-walnut">{b.price}</p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{b.spec}</p>
            <div className="mt-auto pt-6">
              <CtaLink to="/contact" variant="outline" className="h-11">
                Order this blank
              </CtaLink>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function ForditeAndColorwood() {
  return (
    <>
      <Breadcrumbs
        trail={[
          { label: "Home", to: "/" },
          { label: "For Penturners", to: "/for-pen-turners" },
          { label: "Fordite and Colorwood" },
        ]}
      />
      <PageHeader
        eyebrow="Products for penturners"
        title="Fordite and colorwood."
        lede="Blanks poured, pressed and cut in Paul's own shop, alongside Italian colorwood laminated from angle-cut layers. Turned wet or dry, both finish to a deep gloss straight off the lathe."
      >
        <CtaLink to="/contact" variant="outline" className="h-12">
          Ask about availability
        </CtaLink>
      </PageHeader>

      <Section tone="paper" labelledBy="fordite-heading">
        <div className="shell">
          <SectionHeading
            id="fordite-heading"
            eyebrow="Shop-made fordite pen blanks"
            title="Seven colours, seventy to ninety layers."
            lede="Fordite made in my shop using powder-coat paint. There are 7 colors in 70 to 90 layers."
          />
          <BlankGrid items={fordite} />
        </div>
      </Section>

      <Section labelledBy="colorwood-heading">
        <div className="shell">
          <SectionHeading
            id="colorwood-heading"
            eyebrow="Italian colorwood pen blank"
            title="Angle-cut laminated colour."
            lede="Italian colorwood made from multiple angle-cut layers. Shipping is $6 for 1 to 5, $11 for 6 to 25, and $19 for 26 or more."
          />
          <BlankGrid items={colorwood} />
          <p className="mt-8 max-w-2xl text-sm text-muted-foreground">
            Buying for a club or a class? Tell Paul how many you need and he will quote the batch with the right
            shipping tier applied.
          </p>
        </div>
      </Section>

      <FinalCTA
        heading="Want a blank cut to your own dimensions?"
        body="Paul pours fordite in small batches and can size blanks for larger kits on request."
        label="Start a conversation"
      />
    </>
  );
}
